import { useFormControllerContext } from "@/contexts";
import {
    Radio as NextUIRadio,
    RadioGroup as NextUIRadioGroup,
    cn,
} from "@heroui/react";
import type { FieldValues, UseControllerProps } from "react-hook-form";
import { useController as useFieldController } from "react-hook-form";

interface RadioOption {
    label: string;
    value: string;
}

interface RadioGroupProps<T extends FieldValues> {
    label?: string;
    fieldName: UseControllerProps<T>["name"];
    options: RadioOption[];
    isRequired?: boolean;
    isDisabled?: boolean;
    orientation?: "horizontal" | "vertical";
    className?: string;
    isViewOnly?: boolean;
}

const RadioGroup = <T extends FieldValues>({
    label,
    fieldName,
    options,
    isRequired,
    isDisabled,
    orientation = "horizontal",
    className,
    isViewOnly,
}: RadioGroupProps<T>) => {
    const formController = useFormControllerContext();

    const fieldController = useFieldController<T>({
        control: formController.control,
        name: fieldName,
    });

    // Get error state and message from the field controller
    const isInvalid = !!fieldController.fieldState.error;
    const errorMessage = fieldController.fieldState.error?.message;

    return (
        <div
            className={cn(
                "flex flex-col gap-1",
                isViewOnly && "pointer-events-none opacity-70",
                className,
            )}
        >
            <NextUIRadioGroup
                label={label}
                orientation={orientation}
                value={fieldController.field.value}
                onValueChange={fieldController.field.onChange}
                isRequired={isRequired}
                isDisabled={isDisabled}
                isInvalid={isInvalid}
                errorMessage={errorMessage}
                // Add this to ensure validation state gets updated
                onBlur={fieldController.field.onBlur}
                color={isInvalid ? "danger" : "primary"}
                name={fieldName}
            >
                {options.map((option) => (
                    <NextUIRadio key={option.value} value={option.value}>
                        {option.label}
                    </NextUIRadio>
                ))}
            </NextUIRadioGroup>
        </div>
    );
};

export { RadioGroup };
